import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../../config/api';
import GlobalHeader from '../../../../components/GlobalHeader.jsx';
import ImageCard from '../components/ImageCard.jsx';
import ReadOnlyEventDetailsCard from '../components/ReadOnlyEventDetailsCard.jsx';
import { DEFAULT_IMAGE_DATA_URI } from '../../../../components/imageUtils';
import LoginPopup from '../../../../components/LoginPopup.jsx';
import { authService } from '../../../../services/auth';
import '../css/EventDetails.css';

const EventDetailsForPurchase = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [pendingPackageIndex, setPendingPackageIndex] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [loadingAuth, setLoadingAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);

  const fetchEventDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/shared/events/${eventId}`);

      if (!response.ok) {
        if (response.status === 404) {
          setError('Event not found');
          return;
        }
        throw new Error('Failed to load event details');
      }

      const foundEvent = await response.json();

      const formatDate = (dateString) => {
        if (!dateString) return 'Date not specified';
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return 'Date not specified';
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      };

      const formattedDate = formatDate(
        foundEvent.startdate || foundEvent.start_date || foundEvent.date
      );

      const formattedEvent = {
        ...foundEvent,
        id: foundEvent.id || foundEvent.event_id,
        name: foundEvent.name || foundEvent.event_name || 'Event Name Not Available',
        description:
          foundEvent.description || foundEvent.details || 'No description available.',
        location: foundEvent.location || 'Location not specified',
        date: formattedDate,
        time: foundEvent.time || foundEvent.start_time || 'Time not specified',
        organizer: foundEvent.organizer || 'XPress Events',
        category: foundEvent.category || 'General',
        // file_url is already normalised by backend (processEventWithImage)
        file_url: foundEvent.file_url || DEFAULT_IMAGE_DATA_URI,
        start_date:
          foundEvent.startdate || foundEvent.start_date || new Date().toISOString(),
        end_date:
          foundEvent.enddate || foundEvent.end_date || new Date(Date.now() + 86400000).toISOString(),
        duration: foundEvent.duration || '1 day',
        image: foundEvent.file_url || DEFAULT_IMAGE_DATA_URI,
        // background from normalised schema
        background: foundEvent.background_images_array || foundEvent.background || null,
        // terms and dynamic tabs from normalised schema
        terms_and_conditions: foundEvent.terms_and_conditions || '',
        tab_sections: Array.isArray(foundEvent.tab_sections) ? foundEvent.tab_sections : [],
      };

      setEvent(formattedEvent);

      if (foundEvent.packages) {
        let mappedPackages = [];

        if (Array.isArray(foundEvent.packages)) {
          mappedPackages = foundEvent.packages.map((pkg, index) => {
            console.log('🔍 EventDetailsForPurchase mapping package:', {
              index,
              originalPkg: JSON.stringify(pkg, null, 2)
            });
            
            return {
            packageType: pkg.name || `Package ${index + 1}`,
            numberOfTickets: pkg.max_tickets ?? pkg.NumberOfTickets ?? '',
            soldTickets: pkg.sold_tickets ?? 0,  // ← Add this line
            heldTickets: pkg.held_tickets ?? 0,  // ← Add this line
            packageId: pkg.package_id || pkg.PackageID,  // ← Add this line
            duration: pkg.duration ?? '',
            startDate: pkg.start_sale || pkg.sale_dates?.[0] || '',
            endDate: pkg.end_sale || pkg.sale_dates?.[1] || '',
            pricing:
              pkg.price != null
                ? String(pkg.price)
                : pkg.pricing != null
                ? String(pkg.pricing)
                : '',
            details: pkg.description || '',
            // validity window from normalised schema
            validFromDate: pkg.valid_from_date || pkg.valid_date?.[0] || '',
            validToDate: pkg.valid_to_date || pkg.valid_date?.[1] || '',
            validFromTime: pkg.valid_from_time || pkg.valid_time?.[0] || '',
            validToTime: pkg.valid_to_time || pkg.valid_time?.[1] || '',
          };
        });
        }

        setPackages(mappedPackages);
      } else {
        setPackages([]);
      }
    } catch (err) {
      console.error('Error fetching event details:', err);
      setError('Failed to load event details. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchEventDetails();
  }, [fetchEventDetails]);

  const hasToken = !!localStorage.getItem('token');

  const handleSelectPackage = (index) => {
    const token = localStorage.getItem('token');

    if (!token) {
      setPendingPackageIndex(index);
      setShowLoginPopup(true);
      return;
    }

    navigate(`/customerticketdetails1/${eventId}/${index}`);
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoadingAuth(true);
    setErrorMessage('');

    try {
      const result = await authService.login(username, password);

      if (result.success) {
        // Clear fields on successful login
        setUsername('');
        setPassword('');
        setConfirmPassword('');
        setShowLoginPopup(false);

        // Always redirect to main menu for this page, regardless of profile completion
        if (pendingPackageIndex !== null) {
          navigate(`/customerticketdetails1/${eventId}/${pendingPackageIndex}`);
        } else {
          // If no pending package, redirect to main menu
          navigate('/mainmenu');
        }
      }
    } catch (error) {
      setErrorMessage(error.message || 'Login failed. Please check your credentials and try again.');
    } finally {
      setLoadingAuth(false);
    }
  };

  const isRegisterButtonDisabled = !username || !password;

  const handleRegister = async (e, consent = {}) => {
    e.preventDefault();
    try {
      setLoadingAuth(true);
      setErrorMessage('');

      const response = await fetch(`${API_BASE_URL}/shared/basic-register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: username,
          password: password,
          termsAccepted: consent.termsAccepted === true,
          privacyAccepted: consent.privacyAccepted === true
        })
      });

      const result = await response.json();

      if (result.success) {
        setUsername('');
        setPassword('');
        setConfirmPassword('');
        setErrorMessage('');
        setShowLoginPopup(false);
        
        // Show success popup
        setSuccessMessage('Account created successfully! You can now login with your credentials.');
        setShowSuccessPopup(true);
      } else {
        setErrorMessage(result.error || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setErrorMessage('Registration failed. Please try again.');
    } finally {
      setLoadingAuth(false);
    }
  };

  return (
    <div className="event-details-page">
      <GlobalHeader
        title={event ? event.name : 'Event Details'}
        subtitle="Review event details before purchasing"
        backTo="/eventmenu"
        logoutTo="/"
        hideLogoutButton={!hasToken}
      />

      <main className="event-details-page__main">
        {loading ? (
          <div className="event-details-page__loading">
            <p>Loading event details...</p>
          </div>
        ) : error ? (
          <div className="event-details-page__error">
            <p>{error}</p>
            <button type="button" onClick={fetchEventDetails}>
              Retry
            </button>
          </div>
        ) : (
          <>
            <ImageCard event={event} />
            <ReadOnlyEventDetailsCard
              event={event}
              packages={packages}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              onSelectPackage={handleSelectPackage}
            />
          </>
        )}
      </main>

      {showLoginPopup && (
        <LoginPopup
          onClose={() => setShowLoginPopup(false)}
          isLogin={isLogin}
          setIsLogin={setIsLogin}
          username={username}
          setUsername={setUsername}
          password={password}
          setPassword={setPassword}
          confirmPassword={confirmPassword}
          setConfirmPassword={setConfirmPassword}
          errorMessage={errorMessage}
          loading={loadingAuth}
          handleSubmit={handleLoginSubmit}
          handleRegister={handleRegister}
          handleToggle={() => setIsLogin((prev) => !prev)}
          isRegisterButtonDisabled={isRegisterButtonDisabled}
          showLoginToggle={true}
          handleReset={() => {
            setUsername('');
            setPassword('');
            setConfirmPassword('');
            setErrorMessage('');
          }}
        />
      )}

      {/* Success Popup */}
      {showSuccessPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000,
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #ffffff, #fef9c3)',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            position: 'relative',
            boxShadow: '0 20px 40px rgba(255, 193, 7, 0.25)',
          }}>
            <div style={{
              width: '72px',
              height: '72px',
              margin: '0 auto 20px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #fde68a, #f59e0b)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 20px rgba(245, 158, 11, 0.35)',
            }}>
              <span style={{
                fontSize: '42px',
                color: '#78350f',
                lineHeight: 1,
              }}>
                ✓
              </span>
            </div>

            <h3 style={{
              fontSize: '20px',
              fontWeight: 700,
              margin: '0 0 12px 0',
              color: '#78350f',
            }}>
              Account Created Successfully!
            </h3>

            <p style={{
              fontSize: '14px',
              color: '#4b5563',
              lineHeight: '1.5',
              margin: '0 0 24px 0',
            }}>
              {successMessage}
            </p>

            <button
              style={{
                background: 'linear-gradient(135deg, #facc15, #f59e0b)',
                color: '#78350f',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '999px',
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                boxShadow: '0 10px 20px rgba(250, 204, 21, 0.45)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 14px 24px rgba(250, 204, 21, 0.55)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 10px 20px rgba(250, 204, 21, 0.45)';
              }}
              onClick={() => {
                setShowSuccessPopup(false);
                setShowLoginPopup(true);
              }}
            >
              Login Now
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetailsForPurchase;
